import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query/react'
import { RootState } from '../app/store';
import { refresh } from '../features/auth/authSlice';
import { SearchState } from '../features/search/searchSlice';
import type { AdminStats, Member, MemberType, Practitioner, Role, Session, User } from '../types'

type OAuthToken = {
  access_token: string
  refresh_token: string
}
const getLoginRequest = (
  params: { [key: string]: string }
) => ({
  url: 'connect/token',
  method: 'POST',
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded'
  },
  body: new URLSearchParams({
    client_id: 'app',
    grant_type: 'password',
    scope: 'offline_access',
    ...params,
  })
})

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_ENDPOINT,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    // If we have a token set in state, let's assume that we should be passing it.
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
  mode: 'cors'
})
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions)
  console.debug({
    url: result.meta?.request?.url,
    method: result.meta?.request?.method,
    data: result.data,
    error: result.error
  })
  if (result.error) {
    console.warn('error status:', result.error?.status, result)
    if (result.error!.status === 401) {
      const token = (api.getState() as RootState).auth.refreshToken
        || window.localStorage.getItem('refreshToken')
        || window.sessionStorage.getItem('refreshToken');
      if (token) {
        const foo = await baseQuery(
          getLoginRequest({
            grant_type: 'refresh_token',
            refresh_token: token,
          }),
          api,
          extraOptions
        );
        if (foo.error) {
          console.error(foo.error)
        }
        else {
          api.dispatch(
            refresh({
              accessToken: (foo.data as OAuthToken).access_token,
              refreshToken: (foo.data as OAuthToken).refresh_token,
              rememberMe: !!window.localStorage.getItem('refreshToken')
            })
          )
          // refetch original request
          await baseQuery(args, api, extraOptions)
        }
      }
    }
  }
  return result
}

// Define a service using a base URL and expected endpoints
const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    adminStats: builder.query<AdminStats, void>({
      query: () => 'api/admin/stats',
    }),
    bookSession: builder.mutation<void, string>({
      invalidatesTags: (_result, _error, guid) => [
        { type: 'Session', id: guid },
        { type: 'Session', id: 'booked' as keyof SearchState }
      ],
      query: guid => ({
        url: `api/sessions/${guid}/book`,
        method: 'POST',
      }),
    }),
    checkAvailability: builder.mutation<void, string>({
      query: guid => ({
        url: `api/sessions/${guid}/checkAvailability`,
        method: 'POST',
      }),
    }),
    emailOrganizer: builder.mutation<void, {
      content: string
      guid: string
    }>({
      query: ({ guid, ...body }) => ({
        url: `api/sessions/${guid}/emailOrganizer`,
        method: 'POST',
        body,
      }),
    }),
    endSession: builder.mutation<void, string>({
      invalidatesTags: (_result, _error, guid) => [{ type: 'Session', id: guid }],
      query: guid => ({
        url: `api/sessions/${guid}/end`,
        method: 'POST',
      }),
    }),
    forgotPassword: builder.mutation<void, {
      usernameOrEmail: string
    }>({
      query: body => ({
        url: 'api/auth/forgotpassword',
        method: 'POST',
        body
      }),
    }),
    getMemberInfo: builder.query<Member, void>({
      query: () => 'api/member/info',
    }),
    getPractitionerInfo: builder.query<Practitioner, void>({
      query: () => 'api/practitioner/info'
    }),
    getPractitionerSessions: builder.query<Session[], string | undefined>({
      query: guid => `api/sessions/${guid}/practitioner`
    }),
    getRelatedSessions: builder.query<Session[], string | undefined>({
      providesTags: result =>
        result
          ? [...result.map(({ guid }) => ({ type: 'Related' as const, id: guid })), 'Related']
          : ['Related'],
      query: guid => `api/sessions/${guid}/related`
    }),
    getSessionParticipants: builder.query<{
      guid: string
      name: string
      role: Role
    }[], string | undefined>({
      query: guid => `api/sessions/${guid}/attendees`
    }),
    getSession: builder.query<Session, string | undefined>({
      providesTags: result =>
        result
          ? [{ type: 'Session' as const, id: result.guid }, 'Session']
          : ['Session'],
      query: guid => `api/sessions/${guid}`
    }),
    getToken: builder.mutation<{ token: string }, string | undefined>({
      query: guid => ({
        url: `api/sessions/${guid}/token`,
        method: 'POST'
      })
    }),
    getUserInfo: builder.query<User, void>({
      query: () => 'api/account/userinfo',
    }),
    join: builder.mutation<void, string | undefined>({
      query: guid => ({
        url: `api/sessions/${guid}/join`,
        method: 'POST'
      })
    }),
    login: builder.mutation<OAuthToken, {
      username: string
      password: string
    }>({
      query: body => getLoginRequest({
        grant_type: 'password',
        ...body,
      }),
    }),
    resetPassword: builder.mutation<void, {
      password: string
      token?: string
    }>({
      query: body => ({
        url: 'api/auth/resetpassword',
        method: 'POST',
        body
      }),
    }),
    respondToSurvey: builder.mutation<void, {
      content: string
      guid?: string
    }>({
      query: ({ guid, ...body }) => ({
        url: `api/sessions/${guid}/survey`,
        method: 'POST',
        body,
      }),
    }),
    searchSessions: builder.query<Session[], SearchState>({
      providesTags: (result, _error, args) => {
        const filterTags = Object.keys(args).map(id => ({ type: 'Session' as const, id }))
        return result
          ? [
            ...result.map(({ guid }) => ({
              type: 'Session' as const,
              id: guid
            })),
            'Session',
            ...filterTags
          ]
          : ['Session', ...filterTags]
      },
      query: body => ({
        url: 'api/sessions/search',
        method: 'POST',
        body,
      }),
    }),
    sendSurvey: builder.mutation<void, string | undefined>({
      query: guid => ({
        url: `api/sessions/${guid}/sendSurvey`,
        method: 'POST',
      }),
    }),
    share: builder.mutation<void, { email?: string }>({
      query: body => ({
        url: 'api/share',
        method: 'POST',
        body,
      })
    }),
    signup: builder.mutation<{
      access_token: string
    }, {
      captcha?: string
      email: string
      fullName: string
      type: 'practitioner' | MemberType
      username: string
      password: string
      timezone: string
    }>({
      query: body => ({
        url: 'api/account/signup',
        method: 'POST',
        body,
      }),
    }),
    tags: builder.query<string[], void>({
      query: () => 'api/sessions/tags'
    }),
    unbookSession: builder.mutation<void, string>({
      invalidatesTags: (_result, _error, guid) => [
        { type: 'Session', id: guid },
        { type: 'Session', id: 'booked' as keyof SearchState }
      ],
      query: guid => ({
        url: `api/sessions/${guid}/unbook`,
        method: 'POST',
      }),
    }),
    unwatch: builder.mutation<void, string>({
      invalidatesTags: (_result, _error, guid) => [
        { type: 'Session', id: guid },
        { type: 'Session', id: 'watching' as keyof SearchState }
      ],
      query: guid => ({
        url: `api/sessions/${guid}/unwatch`,
        method: 'POST',
      }),
    }),
    updateMemberInfo: builder.mutation<void, Partial<{
      nickname?: string
      phone?: string
      private?: boolean
    }>>({
      query: body => ({
        url: 'api/member/info',
        method: 'PUT',
        body,
      }),
    }),
    updatePractitionerInfo: builder.mutation<void, {
      phone?: string
    }>({
      query: body => ({
        url: 'api/practitioner/info',
        method: 'PUT',
        body,
      }),
    }),
    watch: builder.mutation<void, string>({
      invalidatesTags: (_result, _error, guid) => [
        { type: 'Session', id: guid },
        { type: 'Session', id: 'watching' as keyof SearchState }
      ],
      query: guid => ({
        url: `api/sessions/${guid}/watch`,
        method: 'POST',
      }),
    }),
  }),
  tagTypes: [
    'Related',
    'Session',
    'User',
  ]
})

export default api

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useAdminStatsQuery,
  useBookSessionMutation,
  useCheckAvailabilityMutation,
  useEmailOrganizerMutation,
  useEndSessionMutation,
  useForgotPasswordMutation,
  useGetMemberInfoQuery,
  useGetPractitionerInfoQuery,
  useGetPractitionerSessionsQuery,
  useGetRelatedSessionsQuery,
  useGetSessionParticipantsQuery,
  useGetSessionQuery,
  useGetTokenMutation,
  useGetUserInfoQuery,
  useJoinMutation,
  useLoginMutation,
  useResetPasswordMutation,
  useSearchSessionsQuery,
  useSendSurveyMutation,
  useShareMutation,
  useSignupMutation,
  useRespondToSurveyMutation,
  useTagsQuery,
  useUnbookSessionMutation,
  useUnwatchMutation,
  useUpdateMemberInfoMutation,
  useUpdatePractitionerInfoMutation,
  useWatchMutation,
} = api